import { useContext, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Link,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SttError from '../../../components/error/SttError';
import SttTable from '../../../sporttia/SttTable';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import useScsService from '../../../services/ScsService';
import { GetScsDeviceSpaceParams } from '../../../types/api/paths/Scs';
import { DeviceSpace, Device } from '../../../types/models/DeviceSpace';
import { useConfirmDialog } from '../../../lib/hooks';
import DeviceSpaceDialog from '../../../components/dialogs/DeviceSpaceDialog';
import { AppContext, SttButtonFab, SttFullDialog } from '../../../sporttia/all';
import useDevicesService from '../../../services/DevicesService';
import { copyToClipboard, getErrorMessage } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type DevicesSpacesProps = {
    open: boolean;
    onClose: () => void;
};
type DevicesSpacesFilterParams = {
    name: string; // Sportcenter id
    trash: boolean;
};

export default function DevicesSpacesDialog({
    open,
    onClose,
}: DevicesSpacesProps) {
    const { useGetScsDevicesCapacities } = useScsService();
    const { useUpdateDeviceSpace } = useDevicesService();
    const cxt = useContext(AppContext)!;
    const scId = cxt?.sc ? cxt.sc?.id : -1;
    const [deviceSpace, setDeviceSpace] = useState<DeviceSpace | null>();
    const [openConfirmDialog, , confirmDialog] = useConfirmDialog();
    const [queryParams, setQueryParams] = useState<GetScsDeviceSpaceParams>({
        page: 1,
        rows: 20,
        name: '',
        trash: false,
    });
    const capacitiesQuery = useGetScsDevicesCapacities(scId, queryParams);
    const { t } = useTranslation();
    const updateDeviceSpaceMutation = useUpdateDeviceSpace({
        onSuccess: () => {
            capacitiesQuery.refetch();
            cxt.showMessage('S', t('deviceSpace.resetSuccess'));
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const renderContent = () => {
        if (capacitiesQuery.isLoading) {
            return (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress />
                </Box>
            );
        }

        if (capacitiesQuery.isError) {
            return <SttError />;
        }

        if (!capacitiesQuery.isSuccess) {
            return null;
        }

        return (
            <>
                <SttTopFilteringControls
                    mb={2}
                    fields={[
                        {
                            caption: t('generic.name'),
                            name: 'name',
                            type: 'text',
                            value: '',
                        },
                    ]}
                    menu={undefined}
                    menuIcon={undefined}
                    filterButton={undefined}
                    downloadButton={undefined}
                    iconButtons={undefined}
                    onFilter={(value: DevicesSpacesFilterParams) => {
                        setQueryParams({
                            ...queryParams,
                            name: value.name,
                            trash: value.trash,
                        });
                    }}
                    trashAction
                    mainAction={undefined}
                />
                <SttTable
                    data={capacitiesQuery?.data}
                    onChangePagination={(page: number) =>
                        setQueryParams({ ...queryParams, page })
                    }
                    onFetch={() => {}}
                    columns={[
                        {
                            title: '#',
                            type: 'tooltip',
                            value: (row: DeviceSpace) => ({
                                label: '#',
                                value: row.id,
                            }),
                            onClick: (row: DeviceSpace) =>
                                copyToClipboard(row.id.toString(), () =>
                                    cxt.showMessage(
                                        'S',
                                        t('copiedToClipboard'),
                                    ),
                                ),
                        },
                        {
                            title: t('generic.name'),
                            name: 'name',
                            field: 'name',
                            type: 'link',
                            align: 'center',
                            value: (row: DeviceSpace) => (
                                <Link
                                    onClick={() => setDeviceSpace(row)}
                                    sx={{
                                        color: 'var(--brand-text)',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {row.name}
                                </Link>
                            ),
                        },
                        {
                            title: t('deviceSpace.currentCapacity'),
                            name: 'currentCapacity',
                            field: 'currentCapacity',
                            align: 'center',
                            value: (row: DeviceSpace) => row?.currentCapacity,
                        },
                        {
                            title: t('deviceSpace.maximumCapacity'),
                            name: 'maximumCapacity',
                            field: 'maximumCapacity',
                            align: 'center',
                            value: (row: DeviceSpace) => row?.maximumCapacity,
                        },
                        {
                            field: 'devices',
                            title: t('generic.devices'),
                            align: 'center',
                            value: (row: DeviceSpace) => (
                                <Box mt={1}>
                                    {row?.devices?.map((device: Device) => (
                                        <Chip
                                            key={device?.id}
                                            size="small"
                                            label={device?.name}
                                        />
                                    ))}
                                </Box>
                            ),
                        },
                        {
                            align: 'center',
                            value: (row: DeviceSpace) => (
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        style={{ borderRadius: 5, margin: 5 }}
                                        onClick={() => {
                                            openConfirmDialog({
                                                title: 'Reset',
                                                content: t(
                                                    'deviceSpace.resetConfirmation',
                                                ),
                                                onConfirm: () => {
                                                    updateDeviceSpaceMutation.mutate(
                                                        {
                                                            deviceSpaceId:
                                                                row.id,
                                                            params: {
                                                                currentCapacity: 0,
                                                            },
                                                        },
                                                    );
                                                },
                                            });
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            ),
                        },
                    ]}
                />
                <SttButtonFab
                    permission={PERMISSIONS.DEVICE.MODIFY}
                    onClick={() =>
                        setDeviceSpace({
                            id: -1,
                            created: '',
                            maximumCapacity: 0,
                            currentCapacity: 0,
                            name: '',
                            devices: [],
                        })
                    }
                />
                {deviceSpace && (
                    <DeviceSpaceDialog
                        item={deviceSpace}
                        onSave={() => {
                            capacitiesQuery.refetch();
                        }}
                        onClose={() => {
                            setDeviceSpace(null);
                        }}
                    />
                )}
                {confirmDialog}
            </>
        );
    };

    return (
        <SttFullDialog
            open={open}
            onClose={onClose}
            title={t('deviceSpace.title')}
        >
            <Container>
                <Box my={3}>
                    <Paper style={{ padding: 25 }}>{renderContent()}</Paper>
                </Box>
            </Container>
        </SttFullDialog>
    );
}
